"use client";

import * as React from "react";
import { Moon, Sun, MoonStar } from "lucide-react";
import { cn } from "@/lib/utils/utils";

type OrkestriaTheme = "light" | "dark" | "night";
const STORAGE_KEY = "orkestria-theme";

function readStored(): OrkestriaTheme {
  if (typeof window === "undefined") return "light";
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === "dark" || v === "night" || v === "light") return v;
  return "light";
}

function apply(t: OrkestriaTheme) {
  if (typeof document === "undefined") return;
  const r = document.documentElement;
  r.classList.remove("dark", "night");
  if (t === "dark") r.classList.add("dark");
  else if (t === "night") r.classList.add("night");
  r.setAttribute("data-theme", t);
}

/**
 * Theme switcher para /docs. Compatible con el sistema Orkestria (clases
 * `dark` / `night` en `<html>` + localStorage `orkestria-theme`). El switcher
 * del dashboard sigue funcionando porque escucha el evento `storage`.
 */
export function DocsThemeSwitcher() {
  const [theme, setTheme] = React.useState<OrkestriaTheme>("light");

  React.useEffect(() => {
    setTheme(readStored());
  }, []);

  const change = (t: OrkestriaTheme) => {
    setTheme(t);
    try {
      window.localStorage.setItem(STORAGE_KEY, t);
    } catch {}
    apply(t);
    // Notifica a otras tabs / contextos.
    window.dispatchEvent(
      new StorageEvent("storage", { key: STORAGE_KEY, newValue: t }),
    );
  };

  const options: { id: OrkestriaTheme; label: string; icon: React.ElementType }[] = [
    { id: "light", label: "Claro", icon: Sun },
    { id: "dark", label: "Azul oscuro", icon: Moon },
    { id: "night", label: "Noche", icon: MoonStar },
  ];

  return (
    <div
      role="radiogroup"
      aria-label="Tema"
      className="inline-flex items-center gap-0.5 rounded-md border border-[var(--border)] bg-[var(--card)] p-0.5"
    >
      {options.map((o) => {
        const Icon = o.icon;
        const active = theme === o.id;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={active}
            title={o.label}
            onClick={() => change(o.id)}
            className={cn(
              "inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded transition-colors",
              active
                ? "bg-[var(--primary)]/10 text-[var(--primary)]"
                : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]",
            )}
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        );
      })}
    </div>
  );
}
